<template>
  <div class="image-processing">
    <el-steps :active="currentStep" finish-status="success" align-center>
      <el-step 
        title="圖片上傳" 
        description="上傳圖片並獲取裁剪座標"
        @click.native="goToStep(0)"
        class="clickable-step">
      </el-step>
      <el-step 
        title="圖片裁剪" 
        description="根據座標裁剪圖片"
        @click.native="goToStep(1)"
        class="clickable-step">
      </el-step>
      <el-step 
        title="表格識別" 
        description="識別裁剪後的表格內容"
        @click.native="goToStep(2)"
        class="clickable-step">
      </el-step>
    </el-steps>

    <div class="step-content">
      <!-- Step 1: Image Upload -->
      <div v-show="currentStep === 0" class="step-container">
        <div class="upload-card">
          <el-upload
            class="image-uploader"
            action="#"
            :show-file-list="false"
            :auto-upload="false"
            :on-change="handleImageChange"
            drag
            :on-error="handleUploadError"
            accept="image/jpeg,image/png,image/jpg">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">將文件拖到此處，或<em>點擊上傳</em></div>
            <div class="el-upload__tip">支援 JPG、PNG 格式的圖片文件</div>
          </el-upload>
          
          <!-- 圖片預覽區域 -->
          <div v-if="imageUrl" class="preview-section">
            <div class="preview-box">
              <div class="preview-title">原始圖片：</div>
              <div class="preview-container">
                <img 
                  :src="imageUrl" 
                  class="preview-image clickable-image" 
                  @click="showPreview(imageUrl)"
                  title="點擊放大預覽"
                >
              </div>
            </div>
            <div class="preview-box" v-if="imageBase64">
              <div class="preview-title">處理後圖片：</div>
              <div class="preview-container">
                <img 
                  :src="'data:image/jpeg;base64,' + imageBase64" 
                  class="preview-image clickable-image"
                  @click="showPreview('data:image/jpeg;base64,' + imageBase64)"
                  title="點擊放大預覽"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="result-info">
          <div v-if="detectResult" class="coordinates-box">
            <p>檢測結果：</p>
            <el-input
              type="textarea"
              :rows="2"
              readonly
              :value="detectionsString"
            ></el-input>
            <el-button type="text" icon="el-icon-document-copy" @click="copyCoordinates">
              複製座標
            </el-button>
          </div>
        </div>
        <div class="action-buttons">
          <el-button type="primary" @click="nextStep">下一步</el-button>
        </div>
      </div>

      <!-- Step 2: Image Crop -->
      <div v-show="currentStep === 1" class="step-container">
        <div class="upload-section">
          <el-upload
            class="image-uploader"
            action="#"
            :show-file-list="false"
            :auto-upload="false"
            :on-change="handleCropImageChange"
            drag
            :on-error="handleUploadError"
            accept="image/jpeg,image/png,image/jpg">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">將文件拖到此處，或<em>點擊上傳</em></div>
            <div class="el-upload__tip">支援 JPG、PNG 格式的圖片文件</div>
          </el-upload>

          <div class="crop-input-container">
            <p>請輸入裁剪座標：</p>
            <el-input
              type="textarea"
              v-model="cropDataInput"
              :rows="3"
              placeholder="請輸入裁剪座標，格式如：63.0,137.0,626.0,137.0,626.0,932.0,63.0,930.0,0,0.99"
            ></el-input>
          </div>
        </div>

        <!-- 預覽區域 -->
        <div v-if="cropImageUrl" class="preview-section">
          <div class="preview-box">
            <div class="preview-title">原始圖片：</div>
            <div class="preview-container">
              <img 
                :src="cropImageUrl" 
                class="preview-image clickable-image"
                @click="showPreview(cropImageUrl)"
                title="點擊放大預覽"
              >
            </div>
          </div>
          <div class="preview-box" v-if="croppedImage">
            <div class="preview-title">處理後圖片：</div>
            <div class="preview-container">
              <img 
                :src="croppedImage" 
                class="preview-image clickable-image"
                @click="showPreview(croppedImage)"
                title="點擊放大預覽"
              >
            </div>
          </div>
        </div>

        <div class="action-buttons">
          <el-button @click="previousStep">上一步</el-button>
          <el-button type="primary" @click="handleCrop" :disabled="!cropImageUrl || !cropDataInput.trim()">裁剪</el-button>
          <el-button type="primary" @click="nextStep">下一步</el-button>
        </div>
      </div>

      <!-- Step 3: Table Recognition -->
      <div v-show="currentStep === 2" class="step-container">
        <div class="recognition-upload">
          <el-upload
            class="image-uploader"
            action="#"
            :show-file-list="false"
            :auto-upload="false"
            :on-change="handleRecognitionImageChange"
            drag
            :on-error="handleUploadError"
            accept="image/jpeg,image/png,image/jpg">
            <i class="el-icon-upload"></i>
            <div class="el-upload__text">將文件拖到此處，或<em>點擊上傳</em></div>
            <div class="el-upload__tip">支援 JPG、PNG 格式的圖片文件</div>
          </el-upload>
          
          <!-- 識別圖片預覽區域 -->
          <div v-if="recognitionImage" class="preview-section">
            <div class="preview-box">
              <div class="preview-title">原始圖片：</div>
              <div class="preview-container">
                <img 
                  :src="recognitionImage" 
                  class="preview-image clickable-image"
                  @click="showPreview(recognitionImage)"
                  title="點擊放大預覽"
                >
              </div>
            </div>
            <div class="preview-box" v-if="recognitionImageBase64">
              <div class="preview-title">處理後圖片：</div>
              <div class="preview-container">
                <img 
                  :src="'data:image/jpeg;base64,' + recognitionImageBase64" 
                  class="preview-image clickable-image"
                  @click="showPreview('data:image/jpeg;base64,' + recognitionImageBase64)"
                  title="點擊放大預覽"
                >
              </div>
            </div>
          </div>
        </div>
        <div class="result-container" v-if="recognitionResult">
          <div class="table-result">
            <div class="result-section">
              <h3>單元格索引</h3>
              <div class="result-grid">
                <div v-for="(cell, index) in recognitionResult.cell_idx" :key="'cell-'+index" class="grid-item">
                  {{ cell.join(', ') }}
                </div>
              </div>
            </div>
            
            <div class="result-section">
              <h3>座標信息</h3>
              <div class="result-grid">
                <div v-for="(coor, index) in recognitionResult.coors" :key="'coor-'+index" class="grid-item">
                  {{ coor.join(', ') }}
                </div>
              </div>
            </div>

            <div class="result-section">
              <h3>原始文本結果</h3>
              <pre class="raw-text">{{ recognitionResult.raw_result_text }}</pre>
            </div>

            <div class="action-buttons result-actions">
              <el-button 
                type="primary" 
                size="small"
                @click="downloadResults">
                下載識別結果
              </el-button>
            </div>
          </div>
        </div>
        <div class="action-buttons">
          <el-button @click="previousStep">上一步</el-button>
          <el-button type="primary" @click="handleRecognize" :disabled="!recognitionImage">识别表格</el-button>
          <el-button type="primary" @click="startOver">重新开始</el-button>
        </div>
      </div>
    </div>
    
    <!-- 圖片預覽組件 -->
    <image-preview
      :visible.sync="showImagePreview"
      :image-url="currentPreviewImage"
    />
  </div>
</template>

<script>
import { detectTable, cropImage, recognizeTable } from '@/api/table'
import { Message } from 'element-ui'
import ImagePreview from './ImagePreview.vue'

export default {
  name: 'ImageProcessing',
  components: {
    ImagePreview
  },
  data() {
    return {
      currentStep: 0,
      imageUrl: '',
      imageBase64: '',
      detectResult: null,
      cropImageUrl: '',
      cropImageBase64: '',
      croppedImage: '',
      recognitionResult: null,
      cropCoordinates: null,
      cropDataInput: '',
      cropDataString: '',
      recognitionImage: '',
      recognitionImageBase64: '',
      // 图片预览相关
      showImagePreview: false,
      currentPreviewImage: ''
    }
  },
  computed: {
    detectionsString() {
      if (this.detectResult && this.detectResult.detections) {
        return this.detectResult.detections[0].join(',')
      }
      return ''
    }
  },
  methods: {
    // 显示图片预览
    showPreview(imageUrl) {
      if (imageUrl) {
        this.currentPreviewImage = imageUrl
        this.showImagePreview = true
      }
    },
    
    // 关闭图片预览
    closePreview() {
      this.showImagePreview = false
      this.currentPreviewImage = ''
    },
    
    copyCoordinates() {
      try {
        // 创建临时文本区域
        const textArea = document.createElement('textarea')
        textArea.value = this.detectionsString
        textArea.style.position = 'fixed'
        textArea.style.left = '-9999px'
        textArea.style.top = '0'
        document.body.appendChild(textArea)
        
        // 选择文本
        textArea.select()
        textArea.setSelectionRange(0, 99999) // 对于移动设备
        
        // 尝试使用现代API
        if (navigator.clipboard && window.isSecureContext) {
          navigator.clipboard.writeText(this.detectionsString)
            .then(() => {
              Message.success('坐标已复制到剪贴板')
            })
            .catch(() => {
              // 如果API失败，回退到传统方法
              document.execCommand('copy')
              Message.success('坐标已复制到剪贴板')
            })
        } else {
          // 在不支持 clipboard API 的环境使用传统方法
          document.execCommand('copy')
          Message.success('坐标已复制到剪贴板')
        }
        
        // 清理
        document.body.removeChild(textArea)
      } catch (err) {
        console.error('复制失败:', err)
        Message.error('复制失败，请手动复制')
      }
    },
    handleImageChange(file) {
      const isImage = file.raw.type.startsWith('image/')
      if (!isImage) {
        Message.error('請上傳圖片文件！')
        return
      }

      // 顯示預覽
      this.imageUrl = URL.createObjectURL(file.raw)
      
      // 直接上傳文件
      this.detectTable(file.raw)
    },

    detectTable(file) {
      detectTable(file)
        .then(result => {
          this.detectResult = result.data
          this.imageBase64 = result.data.image_base64
          this.cropCoordinates = result.data.detections[0]
          Message.success('圖片檢測成功')
        })
        .catch(error => {
          Message.error(error.message || '檢測失敗')
        })
    },

    handleUploadError(err, file, fileList) {
      console.error('文件上傳錯誤:', err)
      Message.error('文件上傳失敗，請重試')
    },


    handleRecognitionImageChange(file) {
      const isImage = file.raw.type.startsWith('image/')
      if (!isImage) {
        Message.error('请上传图片文件！')
        return
      }
      this.recognitionImage = URL.createObjectURL(file.raw)
      this.recognitionResult = null
    },

    handleRecognize() {
      if (!this.recognitionImage) {
        Message.error('請先上傳圖片')
        return
      }

      // 從 URL 獲取圖片並轉換為 File 對象
      fetch(this.recognitionImage)
        .then(response => response.blob())
        .then(blob => {
          const file = new File([blob], 'image.jpg', { type: 'image/jpeg' })
          return recognizeTable(file)
        })
        .then(result => {
          this.recognitionResult = result.data
          // 如果後端返回了處理後的圖片
          if (result.data.image_base64) {
            this.recognitionImageBase64 = result.data.image_base64
          }
          Message.success('識別成功')
        })
        .catch(error => {
          Message.error(error.message || '識別失敗')
        })
    },

    nextStep() {
      if (this.currentStep < 2) {
        this.currentStep++
        // 如果從第一步跳轉到第二步，且有檢測結果，則自動填入座標
        if (this.currentStep === 1 && this.detectResult && this.detectResult.detections) {
          this.cropDataInput = this.detectResult.detections[0].join(',')
        }
      }
    },

    previousStep() {
      if (this.currentStep > 0) {
        this.currentStep--
        // 只在返回第一步时清除识别结果
        if (this.currentStep === 1) {
          this.recognitionResult = null
        }
      }
    },

    handleCropImageChange(file) {
      const isImage = file.raw.type.startsWith('image/')
      if (!isImage) {
        Message.error('請上傳圖片文件！')
        return
      }

      // 顯示預覽
      this.cropImageUrl = URL.createObjectURL(file.raw)
      
      // 將圖片轉換為 base64
      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropImageBase64 = e.target.result.split(',')[1] // 移除 data:image/jpeg;base64, 前綴
      }
      reader.readAsDataURL(file.raw)
    },

    handleCrop() {
      if (!this.cropDataInput.trim() || !this.cropImageBase64) {
        Message.error('請上傳圖片並輸入裁剪座標')
        return
      }

      cropImage({
        base64Img: this.cropImageBase64,
        cropData: this.cropDataInput
      })
        .then(result => {
          this.croppedImage = `data:image/jpeg;base64,${result.data}`
          Message.success('裁剪成功')
          
          // 自動下載裁剪後的圖片
          this.downloadCroppedImage(result.data)
        })
        .catch(error => {
          Message.error(error.message || '裁剪失敗')
        })
    },
    downloadCroppedImage(base64Data) {
      try {
        // 创建文件名
        const fileName = `cropped_image_${new Date().getTime()}.jpg`;
        
        // 检测是否为macOS Safari 或者是否为不安全的HTTP连接
        const isMacOSSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) && 
                               navigator.platform.indexOf('Mac') > -1;
        const isInsecureConnection = location.protocol === 'http:';
        
        if (isMacOSSafari || isInsecureConnection) {
          // macOS Safari 或 HTTP连接使用data URL方法，避免blob URL的0字节问题
          this.downloadWithDataUrl(base64Data, fileName);
        } else {
          // HTTPS环境下的其他浏览器使用blob URL方法
          this.downloadWithBlobUrl(base64Data, fileName);
        }
      } catch (error) {
        console.error('下載圖片失敗:', error);
        Message.error('下載圖片失敗，請稍後重試');
      }
    },

    // 使用data URL下载（适用于macOS Safari）
    downloadWithDataUrl(base64Data, fileName) {
      try {
        // 验证base64数据
        if (!base64Data || base64Data.trim() === '') {
          console.error('Base64数据为空');
          Message.error('圖片數據為空，無法下載');
          return;
        }
        
        // 清理base64数据，移除可能的前缀
        let cleanBase64 = base64Data;
        if (base64Data.includes(',')) {
          cleanBase64 = base64Data.split(',')[1];
        }
        
        // 验证base64格式
        try {
          atob(cleanBase64);
        } catch (e) {
          console.error('Base64格式无效:', e);
          Message.error('圖片數據格式錯誤');
          return;
        }
        
        console.log('Base64数据长度:', cleanBase64.length);
        
        // 创建data URL
        const dataUrl = `data:image/jpeg;base64,${cleanBase64}`;
        
        // 创建下载链接
        const link = document.createElement('a');
        link.href = dataUrl;
        link.download = fileName;
        link.style.display = 'none';
        
        // 添加到DOM并立即触发下载
        document.body.appendChild(link);
        
        // 确保在下一个事件循环中触发点击
        setTimeout(() => {
          link.click();
          
          // 延迟清理
          setTimeout(() => {
            document.body.removeChild(link);
            Message.success('圖片下載成功');
          }, 200);
        }, 10);
        
      } catch (error) {
        console.error('Data URL下載失敗:', error);
        // 如果data URL失败，尝试blob URL
        this.downloadWithBlobUrl(base64Data, fileName);
      }
    },

    // 使用blob URL下载（适用于其他浏览器）
    downloadWithBlobUrl(base64Data, fileName) {
      try {
        // 验证base64数据
        if (!base64Data || base64Data.trim() === '') {
          console.error('Base64数据为空');
          Message.error('圖片數據為空，無法下載');
          return;
        }
        
        // 清理base64数据，移除可能的前缀
        let cleanBase64 = base64Data;
        if (base64Data.includes(',')) {
          cleanBase64 = base64Data.split(',')[1];
        }
        
        console.log('Blob方法 - Base64数据长度:', cleanBase64.length);
        
        // 将base64转换为Blob
        const byteCharacters = atob(cleanBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/jpeg' });
        
        console.log('创建的Blob大小:', blob.size, 'bytes');
        
        // 验证blob大小
        if (blob.size === 0) {
          console.error('创建的Blob大小为0');
          Message.error('圖片數據處理失敗');
          return;
        }

        // 确保blob完全创建后再创建URL
        setTimeout(() => {
          const blobUrl = window.URL.createObjectURL(blob);
          
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          
          // 延迟清理，确保下载开始
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
            document.body.removeChild(link);
            Message.success('圖片下載成功');
          }, 300);
        }, 50);
      } catch (error) {
        console.error('Blob URL下載失敗:', error);
        Message.error('下載圖片失敗，請稍後重試');
      }
    },

    startOver() {
      this.currentStep = 0
      this.imageUrl = ''
      this.imageBase64 = ''
      this.detectResult = null
      this.cropImageUrl = ''
      this.cropImageBase64 = ''
      this.croppedImage = ''
      this.recognitionResult = null
      this.cropCoordinates = null
      this.cropDataInput = ''
      this.cropDataString = ''
      this.recognitionImage = ''
      this.recognitionImageBase64 = ''
    },

    goToStep(step) {
      this.currentStep = step
    },

    downloadResults() {
      if (!this.recognitionResult) {
        Message.error('無識別結果可下載')
        return
      }

      try {
        // 创建要下载的内容
        const content = {
          cell_idx: this.recognitionResult.cell_idx,
          coors: this.recognitionResult.coors,
          raw_result_text: this.recognitionResult.raw_result_text
        }

        // 创建Blob对象
        const blob = new Blob([JSON.stringify(content, null, 2)], { 
          type: 'application/json;charset=utf-8'
        })

        // 使用FileSaver.js的方式保存文件
        const fileName = `table_recognition_${new Date().getTime()}.json`
        
        // 確保瀏覽器完全加載了blob數據
        setTimeout(() => {
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          
          // 清理
          window.URL.revokeObjectURL(url)
          document.body.removeChild(link)
          
          Message.success('下載成功')
        }, 100)
      } catch (error) {
        console.error('下載失敗:', error)
        Message.error('下載失敗，請稍後重試')
      }
    }
  }
}
</script>

<style scoped>
.image-processing {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
  min-height: 100vh;
  background-color: #f5f7fa;
}

.step-content {
  margin-top: 40px;
}

.step-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
}

.image-uploader {
  width: 360px;
  margin: 0 auto;
}

.el-upload {
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.el-upload-dragger {
  width: 100%;
  height: 200px;
}

.el-icon-upload {
  font-size: 67px;
  color: #C0C4CC;
  margin-bottom: 16px;
  line-height: 50px;
}

.el-upload__text {
  color: #606266;
  font-size: 14px;
  text-align: center;
}

.el-upload__text em {
  color: #409EFF;
  font-style: normal;
}

.el-upload__tip {
  font-size: 12px;
  color: #909399;
  margin-top: 7px;
  text-align: center;
}

.preview-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.clickable-image {
  cursor: pointer;
  transition: opacity 0.3s ease, transform 0.2s ease;
}

.clickable-image:hover {
  opacity: 0.9;
  transform: scale(1.02);
}

.preview-container {
  width: 100%;
  max-width: 800px;
  min-height: 300px;
  border: 1px solid #eee;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
}

.result-container {
  width: 100%;
  max-width: 800px;
  min-height: 400px;
  padding: 20px;
  border: 1px solid #eee;
  overflow: auto;
}

.action-buttons {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.loading-text {
  color: #909399;
  font-size: 14px;
}

.preview-section {
  margin-top: 20px;
  width: 100%;
  display: flex;
  gap: 20px;
  justify-content: center;
}

.preview-box {
  flex: 1;
  max-width: 500px;
}

.result-info {
  margin-top: 20px;
  text-align: center;
  width: 100%;
  max-width: 600px;
}

.coordinates-box {
  margin: 15px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
  position: relative;
}

.coordinates-box .el-textarea {
  margin-bottom: 10px;
}

.coordinates-box .el-textarea >>> .el-textarea__inner {
  font-family: monospace;
  background: #fff;
  color: #606266;
}

.coordinates-box .el-button {
  position: absolute;
  right: 15px;
  top: 15px;
}

.table-result {
  width: 100%;
  overflow: auto;
}

.table-result pre {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.upload-section {
  width: 100%;
  max-width: 1000px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 20px;
}

.crop-input-container {
  flex: 1;
  max-width: 600px;
}

.crop-input-container p {
  margin-bottom: 10px;
  color: #606266;
}

.preview-placeholder,
.recognition-placeholder {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  background: #f8f9fa;
  border-radius: 4px;
  color: #909399;
}

.preview-placeholder p,
.recognition-placeholder p {
  font-size: 14px;
}

.recognition-upload {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.recognition-upload .image-uploader {
  width: 300px;
  height: 200px;
  margin: 0 auto;
}

.result-section {
  margin-bottom: 30px;
}

.result-section h3 {
  font-size: 16px;
  color: #303133;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 10px;
  max-height: 200px;
  overflow-y: auto;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.grid-item {
  padding: 8px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  white-space: nowrap;
  overflow: auto;
}

.raw-text {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 4px;
  font-family: monospace;
  font-size: 12px;
  max-height: 300px;
  overflow: auto;
  white-space: pre;
}

.result-actions {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #ebeef5;
}

.clickable-step {
  cursor: pointer;
}

.clickable-step:hover >>> .el-step__title,
.clickable-step:hover >>> .el-step__description {
  color: #409EFF !important;
}

/* 确保已完成步骤的标题保持蓝色 */
.el-step__title.is-success {
  color: #67c23a !important;
}

/* 确保当前步骤的标题保持蓝色 */
.el-step__title.is-process {
  color: #409EFF !important;
}
</style>
